import { CallState } from "@b2bportal/core-types";
import type {
  ActivityAvailability,
  ActivityTagCount,
} from "@b2bportal/rbc-activities-api";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "lodash-es";
import { initialFiltersState, type ActivitiesState } from "../../../store";
import type {
  ActivitiesAvailabilityState,
  ActivitySearchDetails,
  ActivitySearchFormValues,
} from "./types";

export const getActivitiesAvailability = (
  state: ActivitiesState
): ActivitiesAvailabilityState => state.activities.activitiesAvailability;

// Search form selectors
export const getSearchFormValues = (
  state: ActivitiesState
): ActivitySearchFormValues =>
  getActivitiesAvailability(state).searchFormValues;

export const getPlace = (state: ActivitiesState) =>
  getSearchFormValues(state).place;

export const getDateRange = (state: ActivitiesState) =>
  getSearchFormValues(state).dateRange;

export const getTravelers = (state: ActivitiesState) =>
  getSearchFormValues(state).travelers;

// Search state selectors
export const getRequestedSearch = (
  state: ActivitiesState
): ActivitySearchDetails | undefined =>
  getActivitiesAvailability(state).requestedSearch;

export const getCurrentSearch = (
  state: ActivitiesState
): ActivitySearchDetails | undefined => getActivitiesAvailability(state).search;

// Activities list selectors
export const isInitialized = (state: ActivitiesState) =>
  state.activities.activitiesAvailability.initialized;

export const getActivities = (state: ActivitiesState): ActivityAvailability[] =>
  getActivitiesAvailability(state).activities.data?.items ?? [];

export const getActivitiesState = (state: ActivitiesState): CallState =>
  getActivitiesAvailability(state).activities.state;

export const getIsLoadingActivities = (state: ActivitiesState): boolean =>
  getActivitiesState(state) === CallState.InProcess;

export const getHasActivitiesError = (state: ActivitiesState): boolean =>
  getActivitiesState(state) === CallState.Failed;

export const getActivitiesError = (
  state: ActivitiesState
): string | undefined => {
  const activities = getActivitiesAvailability(state).activities;
  return activities.state === CallState.Failed
    ? (activities as any).error
    : undefined;
};

export const getNextPageToken = (state: ActivitiesState): string | null =>
  getActivitiesAvailability(state).activities.data?.nextPageToken ?? null;

export const getTotalCount = (state: ActivitiesState): number | undefined =>
  getActivitiesAvailability(state).activities.data?.totalCount;

export const getSearchId = (state: ActivitiesState): string | null =>
  getActivitiesAvailability(state).activities.data?.searchId ?? null;

export const getDestination = (state: ActivitiesState) =>
  state.activities.activitiesAvailability.search?.place;

export const getCategoryTags = (state: ActivitiesState): ActivityTagCount[] =>
  getActivitiesAvailability(state).activities.data?.categoryTags ?? [];

// Filter selectors
export const getFilters = (state: ActivitiesState) =>
  getActivitiesAvailability(state).filters;

export const getMaxPriceFilter = (state: ActivitiesState) =>
  getFilters(state).maxPrice;

export const getFreeCancellationFilter = (state: ActivitiesState) =>
  getFilters(state).freeCancellation;

export const getCategoriesFilter = (state: ActivitiesState) =>
  getFilters(state).categories;

export const getRatingFilter = (state: ActivitiesState) =>
  getFilters(state).rating;

export const getDurationFilter = (state: ActivitiesState) =>
  getFilters(state).duration;

export const getStartTimeFilter = (state: ActivitiesState) =>
  getFilters(state).startTime;

export const getPopularFeatureTagsFilter = (state: ActivitiesState) =>
  getFilters(state).popularFeatureTags;

export const getPopularFeatureTags = (
  state: ActivitiesState
): ActivityTagCount[] =>
  getActivitiesAvailability(state).activities.data?.popularFeatureTags ?? [];

// Sort selectors
export const getSort = (state: ActivitiesState) =>
  getActivitiesAvailability(state).sort;

// Initialization selectors
export const getInitialized = (state: ActivitiesState): boolean =>
  getActivitiesAvailability(state).initialized;

// Derived selectors
export const getHasActivities = (state: ActivitiesState): boolean =>
  getActivities(state).length > 0;

export const getCanLoadMore = (state: ActivitiesState): boolean =>
  getNextPageToken(state) !== null &&
  !getIsLoadingActivities(state) &&
  !getHasActivitiesError(state);

export const getIsSearchComplete = (state: ActivitiesState): boolean => {
  const formValues = getSearchFormValues(state);
  return Boolean(
    (formValues.place?.id ?? "") !== "" &&
      (formValues.dateRange.from ?? "") !== "" &&
      (formValues.dateRange.until ?? "") !== ""
  );
};

export const getDestinationLabel = createSelector(
  getDestination,
  (destination) => {
    return destination?.label.split(",")[0];
  }
);

export const getMaxPriceFilterOption = (state: ActivitiesState) => {
  const activities = getActivities(state);
  const activitiesPrice = activities.flatMap((activity) => {
    const price = activity.bestPrice?.fiat;
    return price == null ? [] : [price];
  });

  const initialPriceOption = activitiesPrice.pop();

  if (initialPriceOption == null) {
    return;
  }
  const priceFilterOptions = activitiesPrice.reduce(
    (carry, item) => {
      return {
        ...carry,
        max: {
          ...carry.max,
          value: Math.max(item.value, carry.max.value),
        },
      };
    },
    {
      max: initialPriceOption,
    }
  );

  return priceFilterOptions.max;
};

export const getFilterOptions = createSelector(
  getMaxPriceFilterOption,
  getCategoryTags,
  getPopularFeatureTags,
  getRatingFilter,
  (maxPriceOption, categoryTags, popularFeatureTags, rating) => {
    return {
      maxPriceOption,
      categories: categoryTags,
      popularFeatureTags,
      rating,
    };
  }
);

export const hasSetPriceFilterSelector = createSelector(
  getMaxPriceFilter,
  (maxPriceFilter) => {
    return maxPriceFilter !== initialFiltersState.maxPrice;
  }
);

export const hasSetRatingFilter = createSelector(
  getRatingFilter,
  (rating) => rating !== initialFiltersState.rating
);

/** True when categories or popular feature tags filters are set. */
export const hasSetCategoriesFilter = createSelector(
  getCategoriesFilter,
  getPopularFeatureTagsFilter,
  (categories, popularFeatureTags) =>
    categories.length > 0 || popularFeatureTags.length > 0
);

export const hasSetCancellationFilter = createSelector(
  getFreeCancellationFilter,
  (freeCancellation) =>
    freeCancellation !== initialFiltersState.freeCancellation
);

export const hasSetDurationFilter = createSelector(
  getDurationFilter,
  (duration) => duration.length > 0
);

export const hasSetStartTimeFilter = createSelector(
  getStartTimeFilter,
  (startTime) => startTime.length > 0
);

export const hasSetUserRatingFilter = createSelector(
  getFilters,
  (filters) => filters.userRating != null
);

export const amountOfSelectedFilters = createSelector(
  [
    hasSetPriceFilterSelector,
    hasSetRatingFilter,
    hasSetCategoriesFilter,
    hasSetCancellationFilter,
    hasSetDurationFilter,
    hasSetStartTimeFilter,
    hasSetUserRatingFilter,
  ],
  (...args): number => {
    return args.filter((arg: boolean) => arg).length;
  }
);

export const hasSetFiltersSelector = createSelector(
  amountOfSelectedFilters,
  (amount): boolean => {
    return amount > 0;
  }
);

/** True when applied filters differ from the initial defaults — same notion as "filters are active". */
export const hasAppliedFiltersSelector = createSelector(
  getFilters,
  (filters): boolean => {
    return !isEqual(filters, initialFiltersState);
  }
);
